/**
 * SubClientTile — sub-tenant management tile. Design contract §5.6.
 *
 * catalogue-card-v2: adopts the shared `.dp-card` primitive (icon-left
 * head, footer row) instead of a bespoke header-strip layout. Keeps its
 * lifecycle badge and ghost "Manage" CTA.
 *
 * Header: monogram mark (no app mark, no red dot) — reuses `.dp-card__mark`.
 * Body: serif display name | sans context (≤2 lines, clamped)
 * Badge: provisioned (forest) | pending (warn) | suspended (stone)
 * CTA:   ghost "Manage" → /admin/sub-clients/:slug (placeholder in cycle 2)
 */

export type SubClientLifecycle = 'provisioned' | 'pending' | 'suspended';

export interface SubClientData {
  slug: string;
  name: string;
  context?: string;
  lifecycle: SubClientLifecycle;
}

interface SubClientTileProps {
  data: SubClientData;
  onManage?: (slug: string) => void;
}

const LIFECYCLE_BADGE: Record<SubClientLifecycle, { label: string; bg: string; fg: string }> = {
  provisioned: { label: 'Provisioned', bg: 'var(--dp-forest-100)', fg: 'var(--dp-forest-700)' },
  pending:     { label: 'Pending',     bg: 'var(--dp-warn-100)',   fg: 'var(--dp-warn-700)'   },
  suspended:   { label: 'Suspended',   bg: 'var(--dp-stone-200)',  fg: 'var(--dp-stone-700)'  },
};

function Monogram({ name }: { name: string }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase()).join('');
  return (
    <div className="dp-card__mark" aria-hidden="true">
      {initials}
    </div>
  );
}

export default function SubClientTile({ data, onManage }: SubClientTileProps) {
  const badge = LIFECYCLE_BADGE[data.lifecycle];

  return (
    <article className="dp-card dp-card--badged">
      <span className="dp-badge" style={{ background: badge.bg, color: badge.fg }}>
        {badge.label}
      </span>

      <div className="dp-card__head">
        <Monogram name={data.name} />
        <div className="dp-card__text">
          <h3 className="dp-card__title">{data.name}</h3>
          <p className="dp-card__desc">{data.context ?? data.slug}</p>
        </div>
      </div>

      <div className="dp-card__foot">
        <div className="dp-card__footleft">
          <p className="dp-card__meta">{data.slug}</p>
        </div>
        <button
          type="button"
          className="dp-btn dp-btn--ghost"
          onClick={() => onManage?.(data.slug)}
        >
          Manage
        </button>
      </div>
    </article>
  );
}
